import Tooltip from "@mui/material/Tooltip";
import React from "react";
import MyOwnCarIcon from "../../../image/icons/itinerary/MyOwnCarIcon.svg?react";
import {
    normalizeEvaluatedPriceForDisplay,
    resolveItineraryPriceForDisplay,
    resolvePricingEvaluationShowFromPrefix,
    resolvePricingEvaluationShowIncompleteFootnote,
    resolveRawItineraryAggregatePriceChf,
} from "../../../tools/ItineraryPricingEvaluationDisplay.tools";
import { ItineraryType, type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import PriceConversion from "./PriceConversion";

/**
 * Explains the trailing asterisk when the pricing evaluation is incomplete.
 */
export const INCOMPLETE_PRICING_ESTIMATION_TOOLTIP = "L'estimation du prix est partielle : certains segments de l'itinéraire n'ont pas pu être évalués.";

interface ItineraryPriceBadgeProps {
    "itinerary": ItineraryDTO;
    "isEuro":    boolean;
    "className": string;

    /**
     * When set, GA adjusts the displayed total using {@link ItineraryDTO.pricingEvaluation} Swiss CFF rows.
     */
    "pricingPreferences"?: ItineraryPricingPreferencesDTO;
}

/**
 * Shows itinerary total price from pricing API (single amount; no alternative fare UI).
 */
const ItineraryPriceBadge: React.FC<ItineraryPriceBadgeProps> = ( { itinerary, isEuro, className, pricingPreferences } ) => {
    const main = normalizeEvaluatedPriceForDisplay( pricingPreferences !== undefined
        ? resolveItineraryPriceForDisplay( itinerary, pricingPreferences )
        : resolveRawItineraryAggregatePriceChf( itinerary ) );
    if ( main === null ) {
        return null;
    }

    const isBlaBlaItinerary = itinerary.type === ItineraryType.BLABLACAR || itinerary.type === ItineraryType.BLABLADAILY;
    const showIncompleteFootnote = pricingPreferences !== undefined
        ? resolvePricingEvaluationShowIncompleteFootnote( itinerary, pricingPreferences )
        : itinerary.pricingEvaluation?.isComplete === false;
    const showFromPrefix = resolvePricingEvaluationShowFromPrefix( itinerary );

    const priceContent = (
        <span className="inline-flex items-baseline gap-0">
            {isBlaBlaItinerary
                ? (
                    <MyOwnCarIcon
                        aria-label="Covoiturage"
                        className="mr-1 inline shrink-0 w-[12px] h-[12px] self-center"
                        style={{ "color": "currentColor" }}
                    />
                )
                : null}
            <PriceConversion
                chooseEuro={isEuro}
                className="text-inherit"
                compact={true}
                price={main}
                showFromPrefix={showFromPrefix === true}
            />
            {showIncompleteFootnote
                ? (
                    <span
                        aria-hidden={true}
                        className="text-inherit"
                    >
                        *
                    </span>
                )
                : null}
        </span>
    );

    if ( showIncompleteFootnote !== true ) {
        return (
            <span className={className}>
                {priceContent}
            </span>
        );
    }

    return (
        <Tooltip
            arrow
            placement="top"
            slotProps={{ "popper": { "sx": { "zIndex": 2147483647 } } }}
            title={INCOMPLETE_PRICING_ESTIMATION_TOOLTIP}
        >
            <span className={`inline-flex cursor-help ${ className }`}>
                {priceContent}
            </span>
        </Tooltip>
    );
};

export default ItineraryPriceBadge;
